import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PORTAL_REQUEST_STATUSES, type PortalMe, type PortalProduct, type PortalRequest } from "@wms/domain";
import { Alert, App, Button, DatePicker, Drawer, Form, Input, InputNumber, List, Select, Space, Spin, Typography, Upload } from "antd";
import type dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, describeError, errorMessage } from "../api/client";
import { downloadPortalFile, portalApi } from "../api/portal";
import {
  CHANGE_REQUEST_STATUSES,
  CREATE_DENIED,
  EDITABLE_STATUSES,
  type Catalog,
  type LineForm,
  type RequestKind,
  afterPackageChange,
  afterProductChange,
  lineFieldErrors,
  missingProductIds,
  packageLabel,
  packageTotalText,
  packagesProblem,
  productLabel,
  productMatches,
  quantityProblem,
  toFormValues,
  toPayloadLines,
} from "./requestForm";
import { DateTimeField } from "../components/DateTimeField";
import { GuardedInput } from "../components/GuardedInput";
import { taxIdProblem } from "../components/kzRequisites";
import { usePortalStore } from "./store";

/** What the drawer shows: a new request of a kind, or one existing request by id. */
export type DrawerTarget = { mode: "new"; kind: RequestKind } | { mode: "open"; id: string };

type Lookup = (id?: string | null) => PortalProduct | undefined;

/**
 * CW31-B03: the card of a request. An existing request is read in full (its lines included) every time it is opened —
 * never from a cache or from the short answer of an action — and its form is mounted only then, with its own form
 * instance keyed by the request, its revision and status: the values of another request or of an empty new form cannot
 * stay behind. After an action the card is read again and remounts with the actual status, revision and buttons.
 */
export function RequestDrawer({ target, onClose }: { target: DrawerTarget | null; onClose: () => void }) {
  const openId = target?.mode === "open" ? target.id : null;
  const detail = useQuery({
    queryKey: ["portal", "requests", openId],
    queryFn: () => portalApi.get<PortalRequest>(`/requests/${openId}`),
    enabled: !!openId,
    staleTime: 0,
    gcTime: 0,
    // read again only after this person's own action: a background refresh never replaces unsaved input
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });
  const request = openId ? (detail.data ?? null) : null;
  const title = request
    ? `Заявка ${request.number} · ${PORTAL_REQUEST_STATUSES[request.status] ?? request.status}`
    : target?.mode === "new"
      ? target.kind === "outbound"
        ? "Заказать отгрузку"
        : "Сообщить о поступлении"
      : "Заявка";
  return (
    <Drawer open={!!target} onClose={onClose} width={720} title={title} destroyOnHidden>
      {target?.mode === "new" && <RequestForm key={`new:${target.kind}`} request={null} kind={target.kind} onClose={onClose} />}
      {openId && detail.isPending && (
        <div style={{ padding: 32, textAlign: "center" }} data-testid="portal-request-loading">
          <Spin />
          <div style={{ marginTop: 8 }}>Загружаем заявку…</div>
        </div>
      )}
      {openId && detail.isError && (
        <Alert
          type="error"
          showIcon
          message="Не удалось открыть заявку"
          description={errorMessage(detail.error)}
          action={<Button onClick={() => void detail.refetch()}>Повторить</Button>}
        />
      )}
      {request && <RequestForm key={`${request.id}:${request.revision}:${request.status}`} request={request} kind={request.kind} onClose={onClose} />}
    </Drawer>
  );
}

function isValidationReject(e: unknown): boolean {
  return typeof e === "object" && e !== null && "errorFields" in e;
}

/** «Удобный выбор времени» (04.11.2026): the time of a request is typed on the clock of the chosen warehouse. */
function PortalDateTime({ warehouses, ...rest }: { warehouses: PortalMe["warehouses"]; value?: Dayjs | null; onChange?: (v: Dayjs | null) => void; id?: string; disabled?: boolean }) {
  const form = Form.useFormInstance();
  const warehouseId = Form.useWatch("warehouse_id", form) as string | undefined;
  const clock = warehouses.find((w) => w.id === warehouseId) ?? null;
  return <DateTimeField {...rest} clock={clock} planning />;
}

function RequestForm({ request, kind, onClose }: { request: PortalRequest | null; kind: RequestKind; onClose: () => void }) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { me, can } = usePortalStore();
  const [form] = Form.useForm();
  // applied once, at mount: this form instance lives exactly as long as this request (and revision) is open
  const [initialValues] = useState(() => toFormValues(request, kind, me?.warehouses[0]?.id));
  const [conflict, setConflict] = useState(false);

  // ---- the catalog: the first page, the products of this request by id, whatever a search has found -------------
  const base = useQuery({ queryKey: ["portal", "products"], queryFn: () => portalApi.get<PortalProduct[]>("/products") });
  const baseCatalog = useMemo<Catalog>(() => Object.fromEntries((base.data ?? []).map((p) => [p.id, p])), [base.data]);
  const missing = useMemo(() => missingProductIds(request, baseCatalog), [request, baseCatalog]);
  const named = useQuery({
    queryKey: ["portal", "products", "ids", missing],
    queryFn: () => portalApi.get<PortalProduct[]>(`/products?${missing.map((id) => `ids=${encodeURIComponent(id)}`).join("&")}`),
    enabled: !base.isPending && missing.length > 0,
  });
  const [found, setFound] = useState<Catalog>({});
  const remember = useCallback((rows: PortalProduct[]) => {
    setFound((prev) => (rows.every((r) => prev[r.id] === r) ? prev : { ...prev, ...Object.fromEntries(rows.map((r) => [r.id, r])) }));
  }, []);
  const catalog = useMemo<Catalog>(
    () => ({ ...baseCatalog, ...Object.fromEntries((named.data ?? []).map((p) => [p.id, p])), ...found }),
    [baseCatalog, named.data, found],
  );
  const catalogRef = useRef(catalog);
  catalogRef.current = catalog;
  const lookup = useCallback<Lookup>((id) => (id ? catalogRef.current[id] : undefined), []);

  // ---- what this person may do with this request ------------------------------------------------------------------
  const editable = !request || EDITABLE_STATUSES.includes(request.status);
  const changeable = !!request && CHANGE_REQUEST_STATUSES.includes(request.status);
  // CW31-B12: a new request needs «создание заявок», an existing one «изменение заявок»; the server checks both again
  const allowed = request ? can("portal.requests.update") : can("portal.requests.create");
  const readOnly = !allowed || (!editable && !changeable);
  const saveLabel = editable ? "Сохранить черновик" : changeable ? "Отправить запрос изменения" : null;
  const statusWords = request ? (PORTAL_REQUEST_STATUSES[request.status] ?? request.status) : "";
  const readOnlyReason = !allowed
    ? request
      ? "Только просмотр: изменить заявку может сотрудник с правом изменения заявок"
      : CREATE_DENIED
    : null;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["portal", "requests"] });
  const showError = (e: unknown) => {
    if (e instanceof ApiError) {
      // CW31-B01: the reason stands next to the field of its line, the typed value stays as it was
      const fields = lineFieldErrors(e.problem.errors);
      if (fields.length) form.setFields(fields);
      if (e.problem.field === "expected_revision") setConflict(true);
    }
    message.error(describeError(e));
  };
  const write = (values: Record<string, unknown>) => {
    const body = {
      ...values,
      expected_at: values.expected_at ? (values.expected_at as dayjs.Dayjs).toISOString() : null,
      lines: toPayloadLines(values.lines as LineForm[] | undefined, catalogRef.current),
      expected_revision: request?.revision,
    };
    return request ? portalApi.put<PortalRequest>(`/requests/${request.id}`, body) : portalApi.post<PortalRequest>("/requests", body);
  };
  const save = useMutation({
    mutationFn: write,
    onSuccess: (saved) => {
      message.success(
        saved.status === "draft"
          ? `Черновик ${saved.number} сохранён`
          : EDITABLE_STATUSES.includes(saved.status)
            ? `Заявка ${saved.number} сохранена: отправьте её на склад, когда всё готово`
            : `Запрос изменения по ${saved.number} передан складу`,
      );
      // the card closes on purpose: the next opening reads the saved request in full
      onClose();
      void refresh();
    },
    onError: showError,
  });
  // CW31-B03: «Отправить на склад» sends what the person sees — unsaved changes are saved first; afterwards the card
  // is read again (the answer of the action has no lines and never replaces the card)
  const wrote = useRef(false);
  const submit = useMutation({
    mutationFn: async () => {
      wrote.current = false;
      if (!request) return null;
      if (allowed && editable && form.isFieldsTouched()) {
        await write(await form.validateFields());
        wrote.current = true;
      }
      return portalApi.post<PortalRequest>(`/requests/${request.id}/submit`);
    },
    onSuccess: (sent) => {
      if (sent) message.success(`Заявка ${sent.number} отправлена на склад. Состояние: ${PORTAL_REQUEST_STATUSES[sent.status] ?? sent.status}`);
      void refresh();
    },
    onError: (e) => {
      if (isValidationReject(e)) {
        message.error("Проверьте поля заявки: ошибки отмечены красным");
        return;
      }
      showError(e);
      // the changes were saved but the sending was refused: the card is read again with its new revision; a refused
      // save keeps the typed values on screen (a version conflict waits for «Обновить»)
      if (wrote.current) void refresh();
    },
  });

  const deliveryKind = (Form.useWatch("delivery_kind", form) ?? initialValues.delivery_kind) as string | undefined;
  const products = base.data ?? [];

  return (
    <>
      {request && changeable && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Заявка уже отправлена"
          description="Изменение состава после отправки не переписывает задания склада: будет создан запрос изменения, который рассмотрит оператор."
        />
      )}
      {request && !editable && !changeable && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }} message={`Заявка в состоянии «${statusWords}»: изменить её нельзя`} data-testid="portal-request-closed" />
      )}
      {conflict && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Заявку изменили, пока она была открыта"
          description="Ваши изменения не сохранены. Обновите карточку — откроется актуальная версия, затем внесите изменения снова."
          action={<Button onClick={() => void refresh()}>Обновить</Button>}
          data-testid="portal-request-conflict"
        />
      )}
      {request?.operator_comment && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="Ответ склада" description={request.operator_comment} />}
      {request && <RequestAttachments requestId={request.id} closed={["cancelled", "rejected"].includes(request.status)} canEdit={can("portal.requests.update")} />}
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={(values) => save.mutate(values)}
        onFinishFailed={() => message.error("Проверьте поля заявки: ошибки отмечены красным")}
        scrollToFirstError
        disabled={readOnly}
        data-testid="portal-request-form"
      >
        <Form.Item name="kind" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="warehouse_id" label="Склад" rules={[{ required: true, message: "Выберите склад" }]}>
          <Select options={(me?.warehouses ?? []).map((w) => ({ value: w.id, label: `${w.code} — ${w.name}` }))} disabled={!!request} />
        </Form.Item>
        <Form.Item name="expected_at" label={kind === "outbound" ? "Желаемая дата отгрузки" : "Ожидаемая дата и время прибытия"}>
          <PortalDateTime warehouses={me?.warehouses ?? []} />
        </Form.Item>
        {kind === "inbound" ? (
          <Space style={{ width: "100%" }} styles={{ item: { flex: 1 } }}>
            <Form.Item name="supplier_name" label="Поставщик">
              <Input />
            </Form.Item>
            <Form.Item name="supplier_invoice" label="Номер накладной поставщика">
              <Input />
            </Form.Item>
          </Space>
        ) : (
          <>
            <Form.Item name="recipient_name" label="Получатель" rules={[{ required: true, message: "Укажите получателя" }]}>
              <Input />
            </Form.Item>
            {/* CW31 (customer 31.10.2026): the warehouse finds the recipient's card by its БИН/ИИН — required, checked */}
            <Form.Item
              name="recipient_bin"
              label="БИН/ИИН получателя"
              extra={
                me?.partner?.bin && !readOnly ? (
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0 }}
                    data-testid="portal-recipient-self"
                    onClick={() => form.setFieldsValue({ recipient_name: me.partner.name, recipient_bin: me.partner.bin })}
                  >
                    Получатель — мы сами
                  </Button>
                ) : (
                  "По нему склад находит карточку получателя"
                )
              }
              rules={[
                { required: true, whitespace: true, message: "Укажите БИН/ИИН получателя" },
                {
                  validator: (_rule, value) => {
                    const problem = taxIdProblem(String(value ?? "").replace(/\s+/g, ""), null);
                    return problem ? Promise.reject(new Error(problem)) : Promise.resolve();
                  },
                },
              ]}
            >
              {/* UXW-66 (option «Запрет недопустимого ввода»): the same policy as the БИН/ИИН of the cards — a letter or
                  a 13th digit is not taken, a too long paste is refused whole instead of being cut at 14 characters */}
              {me?.feature_options?.use_input_guard ? (
                <GuardedInput policy={{ kind: "tax_id", label: "БИН/ИИН", spaces: true }} data-testid="portal-recipient-bin" />
              ) : (
                <Input inputMode="numeric" maxLength={14} data-testid="portal-recipient-bin" />
              )}
            </Form.Item>
            <Form.Item name="delivery_kind" label="Как забрать">
              <Select options={[{ value: "delivery", label: "Доставка" }, { value: "pickup", label: "Самовывоз" }]} />
            </Form.Item>
            {deliveryKind !== "pickup" && (
              <Form.Item name="delivery_address" label="Адрес доставки" rules={[{ required: true, message: "Укажите адрес" }]}>
                <Input />
              </Form.Item>
            )}
          </>
        )}
        <Typography.Title level={5}>Товары</Typography.Title>
        <Form.List name="lines">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => (
                <RequestLine
                  key={field.key}
                  name={field.name}
                  kind={kind}
                  products={products}
                  catalog={catalog}
                  catalogLoading={base.isPending || named.isFetching}
                  lookup={lookup}
                  remember={remember}
                  onRemove={() => remove(field.name)}
                />
              ))}
              <Button onClick={() => add({})} icon={<PlusOutlined />} data-testid="portal-add-line">
                Добавить товар
              </Button>
            </>
          )}
        </Form.List>
        <Form.Item name="comment" label="Комментарий складу" style={{ marginTop: 12 }}>
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
      {/* the buttons stay outside the read-only form: «Вернуться» always works, a refused one says why */}
      <Space wrap>
        {saveLabel && (
          <Button type="primary" onClick={() => form.submit()} loading={save.isPending} disabled={readOnly} data-testid="portal-request-save">
            {saveLabel}
          </Button>
        )}
        {request && editable && can("portal.requests.submit") && (
          <Button onClick={() => submit.mutate()} loading={submit.isPending} data-testid="portal-request-submit">
            Отправить на склад
          </Button>
        )}
        <Button onClick={onClose} data-testid="portal-request-close">
          Вернуться
        </Button>
      </Space>
      {readOnlyReason && (
        <Typography.Paragraph type="secondary" style={{ marginTop: 8 }} data-testid="portal-request-readonly">
          {readOnlyReason}
        </Typography.Paragraph>
      )}
    </>
  );
}

/** One line of the request: product → package → number → total in base units, then batch, expiry and comment. */
function RequestLine({
  name,
  kind,
  products,
  catalog,
  catalogLoading,
  lookup,
  remember,
  onRemove,
}: {
  name: number;
  kind: RequestKind;
  products: PortalProduct[];
  catalog: Catalog;
  catalogLoading: boolean;
  lookup: Lookup;
  remember: (rows: PortalProduct[]) => void;
  onRemove: () => void;
}) {
  const form = Form.useFormInstance();
  // useWatch answers only after its effect: the first render reads the store, so a package line opens as one at once
  const watched = Form.useWatch(["lines", name], form) as LineForm | undefined;
  const line = (watched ?? form.getFieldValue(["lines", name]) ?? {}) as LineForm;
  const product = line.storage_object_id ? catalog[line.storage_object_id] : undefined;
  const packages = product?.packages ?? [];
  const pkg = packages.find((p) => p.id === line.package_id);
  const packaged = !!(line.storage_object_id && line.package_id);
  const total = packaged && product ? packageTotalText(line.packages_count, pkg, product.unit) : null;
  const patch = (values: Partial<LineForm>) => {
    for (const [key, value] of Object.entries(values)) form.setFieldValue(["lines", name, key], value);
  };
  const current = () => (form.getFieldValue(["lines", name]) ?? {}) as LineForm;
  const packageOptions = packages.map((p) => ({ value: p.id, label: packageLabel(p, product?.unit ?? "") }));
  if (line.package_id && !pkg) packageOptions.push({ value: line.package_id, label: "Упаковка не найдена в справочнике товара" });

  return (
    <div style={{ border: "1px solid var(--wms-border)", borderRadius: 10, padding: 12, marginBottom: 10 }} data-testid={`portal-line-${name}`}>
      {/* CW31-B21: a package of the former product never travels to the new one (the select's own onChange runs after
          the form has stored the new product) */}
      <Form.Item
        name={[name, "storage_object_id"]}
        label="Товар из каталога"
        extra={product?.shared ? "Общий каталог склада: товар доступен всем клиентам, остатки и документы — только ваши" : undefined}
      >
        <ProductSelect
          products={products}
          catalog={catalog}
          catalogLoading={catalogLoading}
          remember={remember}
          onChange={() => patch(afterProductChange(current(), lookup(current().storage_object_id)))}
        />
      </Form.Item>
      {kind === "inbound" && (
        <Form.Item name={[name, "product_text"]} label="Или новый товар (название)" extra="Оператор склада заведёт карточку и сверит название">
          <Input />
        </Form.Item>
      )}
      {product && packageOptions.length > 0 && (
        <Form.Item name={[name, "package_id"]} label="Упаковка">
          <Select
            allowClear
            placeholder={`Без упаковки — количество в ${product.unit}`}
            options={packageOptions}
            onChange={() => patch(afterPackageChange(current()))}
            style={{ maxWidth: 360 }}
            data-testid={`portal-line-package-${name}`}
          />
        </Form.Item>
      )}
      <Space wrap align="start">
        {packaged ? (
          <Form.Item
            name={[name, "packages_count"]}
            label="Количество упаковок"
            dependencies={[["lines", name, "package_id"]]}
            rules={[
              ({ getFieldValue }) => ({
                validator: (_, value) => {
                  const item = lookup(getFieldValue(["lines", name, "storage_object_id"]));
                  const chosen = item?.packages?.find((p) => p.id === getFieldValue(["lines", name, "package_id"]));
                  const problem = packagesProblem(value, chosen, item);
                  return problem ? Promise.reject(new Error(problem)) : Promise.resolve();
                },
              }),
            ]}
            extra={total ? <span data-testid={`portal-line-total-${name}`}>{total}</span> : undefined}
          >
            <InputNumber stringMode decimalSeparator="," inputMode="decimal" style={{ width: 180 }} data-testid={`portal-line-packages-${name}`} />
          </Form.Item>
        ) : (
          <Form.Item
            name={[name, "quantity"]}
            label={product ? `Количество, ${product.unit}` : "Количество, базовых ед."}
            dependencies={[["lines", name, "storage_object_id"]]}
            rules={[
              ({ getFieldValue }) => ({
                validator: (_, value) => {
                  // CW31-B01: the rule of the unit of the chosen product; nothing is substituted for a wrong number
                  const problem = quantityProblem(value, lookup(getFieldValue(["lines", name, "storage_object_id"])));
                  return problem ? Promise.reject(new Error(problem)) : Promise.resolve();
                },
              }),
            ]}
            extra={!line.storage_object_id && line.product_text ? "Для нового товара — в базовых единицах; упаковку склад укажет, когда заведёт карточку" : undefined}
          >
            <InputNumber stringMode decimalSeparator="," inputMode="decimal" style={{ width: 180 }} data-testid={`portal-line-quantity-${name}`} />
          </Form.Item>
        )}
        <Form.Item name={[name, "batch_code"]} label="Партия">
          <Input style={{ width: 160 }} />
        </Form.Item>
        <Form.Item name={[name, "expiry_date"]} label="Срок годности">
          <DatePicker format="DD.MM.YYYY" />
        </Form.Item>
      </Space>
      <Form.Item name={[name, "comment"]} label="Комментарий к строке">
        <Input />
      </Form.Item>
      <Button danger size="small" onClick={onRemove}>
        Убрать строку
      </Button>
    </div>
  );
}

/**
 * CW31-B19: the product of a line from the catalog of the client — its own cards and the common catalog. The loaded
 * first page is filtered at once; the server is searched too, so a card beyond that page is found by code or name.
 */
function ProductSelect({
  id,
  value,
  onChange,
  products,
  catalog,
  catalogLoading,
  remember,
}: {
  id?: string;
  value?: string | null;
  onChange?: (value: string | undefined) => void;
  products: PortalProduct[];
  catalog: Catalog;
  catalogLoading: boolean;
  remember: (rows: PortalProduct[]) => void;
}) {
  const [term, setTerm] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    const timer = window.setTimeout(() => setQ(term.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [term]);
  const search = useQuery({
    queryKey: ["portal", "products", "q", q],
    queryFn: () => portalApi.get<PortalProduct[]>("/products", { q }),
    enabled: q.length > 0,
  });
  useEffect(() => {
    if (search.data) remember(search.data);
  }, [search.data, remember]);
  const options = useMemo(() => {
    const rows = new Map<string, PortalProduct>();
    for (const p of products) if (productMatches(p, term)) rows.set(p.id, p);
    if (q) for (const p of search.data ?? []) rows.set(p.id, p);
    const list = [...rows.values()].map((p) => ({ value: p.id, label: productLabel(p) }));
    if (value && !rows.has(value)) {
      const known = catalog[value];
      list.unshift({ value, label: known ? productLabel(known) : catalogLoading ? "Товар загружается…" : "Товар больше не доступен в вашем каталоге" });
    }
    return list;
  }, [products, term, q, search.data, value, catalog, catalogLoading]);
  return (
    <Select
      id={id}
      showSearch
      allowClear
      filterOption={false}
      value={value ?? undefined}
      onSearch={setTerm}
      onChange={(next: string | undefined) => {
        setTerm("");
        onChange?.(next);
      }}
      onBlur={() => setTerm("")}
      options={options}
      loading={search.isFetching}
      placeholder="Код, название или штрихкод товара"
      notFoundContent={search.isFetching ? <Spin size="small" /> : term ? "Не найдено: проверьте код или название" : "В каталоге пока нет товаров"}
    />
  );
}

interface RequestAttachment {
  id: string;
  filename: string;
  content_type: string;
  size: number;
  comment: string | null;
  created_at: string;
  url: string;
}

/** 14.3 «вложения»: the documents of the request — a supplier invoice, a photo; the operator sees them on the card. */
function RequestAttachments({ requestId, closed, canEdit }: { requestId: string; closed: boolean; canEdit: boolean }) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const files = useQuery({ queryKey: ["portal", "attachments", requestId], queryFn: () => portalApi.get<RequestAttachment[]>(`/requests/${requestId}/attachments`) });
  const upload = useMutation({
    mutationFn: (file: File) => portalApi.upload<RequestAttachment>(`/requests/${requestId}/attachments`, file),
    onSuccess: () => {
      message.success("Файл приложен к заявке");
      queryClient.invalidateQueries({ queryKey: ["portal", "attachments", requestId] });
    },
    onError: (e) => message.error(errorMessage(e)),
  });
  return (
    <div style={{ marginBottom: 12 }} data-testid="portal-request-attachments">
      <Typography.Text strong>Вложения</Typography.Text>
      <List
        size="small"
        locale={{ emptyText: "Файлов пока нет" }}
        dataSource={files.data ?? []}
        renderItem={(file) => (
          <List.Item
            actions={[
              <Button key="dl" type="link" size="small" onClick={() => downloadPortalFile(file.url, file.filename).catch((e) => message.error(errorMessage(e)))}>
                Скачать
              </Button>,
            ]}
          >
            {file.filename} <Typography.Text type="secondary">({Math.ceil(file.size / 1024)} КБ{file.comment ? `, ${file.comment}` : ""})</Typography.Text>
          </List.Item>
        )}
      />
      {canEdit && !closed && (
        <Upload
          showUploadList={false}
          beforeUpload={(file) => {
            upload.mutate(file as unknown as File);
            return false;
          }}
        >
          <Button icon={<UploadOutlined />} loading={upload.isPending} data-testid="portal-attach-file">
            Приложить файл
          </Button>
        </Upload>
      )}
    </div>
  );
}
